import {
  BuilderSplitCommentRecord,
  ParsedBuilderSplitComment,
  ParsedDeductionEntry,
} from "./types";

const DEDUCTION_MARKER = "|Deduction Explanation|";
const UNASSIGNED_JUSTIFICATION_MARKER = "|Unassigned Bonus Explanation:";
const UNASSIGNED_REFERENCE_MARKER = "|Unassigned Bonus Reference:";
const BUILDER_SPLIT_UPDATE_MARKER = "Builder Split Update:";

export function getLatestRelevantBuilderSplitComment(
  comments: BuilderSplitCommentRecord[]
): BuilderSplitCommentRecord | null {
  const relevant = comments.filter((c) => {
    const text = c.comment || "";
    return (
      text.includes(DEDUCTION_MARKER) ||
      text.includes(UNASSIGNED_JUSTIFICATION_MARKER)
    );
  });

  if (!relevant.length) return null;

  return [...relevant].sort(
    (a, b) => parseLooseDate(b.createdOn).getTime() - parseLooseDate(a.createdOn).getTime()
  )[0];
}

export function parseBuilderSplitComment(
  rawComment: string | undefined | null
): ParsedBuilderSplitComment {
  const text = rawComment || "";
  const deductions: ParsedDeductionEntry[] = [];

  const parts = text.split(DEDUCTION_MARKER).slice(1);

  for (const part of parts) {
    const normalized = part.trim();

    const builderName = readField(normalized, "Builder:", "|");
    const empIdText = readField(normalized, "EmpID:", "|");
    const amountText = readField(normalized, "Amount:", "|");
    const comment = readCommentField(normalized);

    const empId = Number(empIdText);
    const amount = Number(amountText);

    if (!Number.isFinite(empId) || !Number.isFinite(amount) || !comment?.trim()) {
      continue;
    }

    deductions.push({
      empId,
      builderName: builderName?.trim() || "",
      amount,
      comment: comment.trim(),
    });
  }

  const unassignedBonusJustification = readField(
    text,
    UNASSIGNED_JUSTIFICATION_MARKER,
    UNASSIGNED_REFERENCE_MARKER
  )?.trim();

  const unassignedBonusReference = readField(
    text,
    UNASSIGNED_REFERENCE_MARKER,
    null
  )?.trim();

  return {
    deductions,
    unassignedBonusJustification: unassignedBonusJustification || undefined,
    unassignedBonusReference: unassignedBonusReference || undefined,
  };
}

export function stripStructuredBuilderSplitMetadata(
  rawComment: string | undefined | null
): string {
  let text = (rawComment || "").trim();
  if (!text) return "";

  const firstStructuredIndex = findFirstStructuredIndex(text);

  if (firstStructuredIndex === -1) {
    return text;
  }

  text = text.slice(0, firstStructuredIndex).trim();

  if (text.endsWith(BUILDER_SPLIT_UPDATE_MARKER)) {
    text = text.slice(0, -BUILDER_SPLIT_UPDATE_MARKER.length).trim();
  }

  return text.trim();
}

export function buildStructuredBuilderSplitComment(input: {
  baseComments?: string;
  deductions: Array<{
    builderName: string;
    employeeNumber: number;
    deductionAmount: number;
    deductionComment?: string;
  }>;
  unassignedBonusJustification?: string;
  unassignedBonusReference?: string;
}): string {
  const segments: string[] = [];
  const base = stripStructuredBuilderSplitMetadata(input.baseComments || "").trim();

  if (base) {
    segments.push(base);
  }

  const deductionSegments = input.deductions
    .filter(
      (d) =>
        d.deductionAmount > 0 &&
        !!d.deductionComment &&
        d.deductionComment.trim().length >= 3
    )
    .map(
      (d) =>
        `|Deduction Explanation| Builder: ${d.builderName} | EmpID: ${
          d.employeeNumber
        } | Amount: ${d.deductionAmount.toFixed(2)} | Comment: ${d.deductionComment!.trim()}`
    );

  if (deductionSegments.length) {
    segments.push(BUILDER_SPLIT_UPDATE_MARKER);
    segments.push(...deductionSegments);
  }

  if (input.unassignedBonusJustification?.trim()) {
    segments.push(
      `|Unassigned Bonus Explanation: ${input.unassignedBonusJustification.trim()}`
    );
    segments.push(
      `|Unassigned Bonus Reference: ${
        input.unassignedBonusReference?.trim() || "N/A"
      }`
    );
  }

  return segments.join(" ").trim();
}

function findFirstStructuredIndex(text: string): number {
  const indices = [
    text.indexOf(BUILDER_SPLIT_UPDATE_MARKER),
    text.indexOf(DEDUCTION_MARKER),
    text.indexOf(UNASSIGNED_JUSTIFICATION_MARKER),
    text.indexOf(UNASSIGNED_REFERENCE_MARKER),
  ].filter((idx) => idx >= 0);

  return indices.length ? Math.min(...indices) : -1;
}

function readField(
  text: string,
  startToken: string,
  endToken: string | null
): string | undefined {
  const start = text.indexOf(startToken);
  if (start === -1) return undefined;

  const from = start + startToken.length;

  if (endToken === null) {
    return text.slice(from).trim();
  }

  const end = text.indexOf(endToken, from);
  if (end === -1) {
    return text.slice(from).trim();
  }

  return text.slice(from, end).trim();
}

function readCommentField(text: string): string | undefined {
  const marker = "Comment:";
  const idx = text.indexOf(marker);
  if (idx === -1) return undefined;
  return text.slice(idx + marker.length).trim();
}

function parseLooseDate(value: string): Date {
  const normalized = value?.trim();
  if (!normalized) return new Date(0);

  const parts = normalized.split(" ");
  if (parts.length !== 2) {
    const d = new Date(normalized);
    return Number.isNaN(d.getTime()) ? new Date(0) : d;
  }

  const [datePart, timePart] = parts;
  const [month, day, year] = datePart.split("/").map(Number);
  const [hour, minute, second] = timePart.split(":").map(Number);

  const d = new Date(year, (month || 1) - 1, day || 1, hour || 0, minute || 0, second || 0);
  return Number.isNaN(d.getTime()) ? new Date(0) : d;
}